import { useEffect, useMemo, useState } from 'react';
import type { ThemeContent } from './content-types';
import { getApiBaseUrl } from './config';

const CACHE_PREFIX = 'rynora-content::';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Live API theme override key. Cached separately from the JSON manifests
// because it points at /api/theme rather than /content/<path>.
const API_THEME_CACHE_KEY = `${CACHE_PREFIX}api-theme`;
const API_THEME_TTL_MS = 60 * 1000; // 60s — matches the API Cache-Control max-age.

interface CacheEnvelope<T> {
  expiresAt: number;
  value: T;
}

function cacheKey(path: string): string {
  return `${CACHE_PREFIX}${path}`;
}

function readCache<T>(path: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(path));
    if (!raw) return null;
    const env = JSON.parse(raw) as CacheEnvelope<T>;
    if (!env || typeof env.expiresAt !== 'number') return null;
    if (Date.now() > env.expiresAt) return null;
    return env.value;
  } catch {
    return null;
  }
}

function writeCache<T>(path: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    const env: CacheEnvelope<T> = { expiresAt: Date.now() + CACHE_TTL_MS, value };
    window.localStorage.setItem(cacheKey(path), JSON.stringify(env));
  } catch {
    // localStorage may be full or unavailable; non-fatal.
  }
}

/**
 * Fetch a JSON manifest from `/content/<path>`.
 *
 * Order of operations:
 *   1. Return cached value (≤ 5 minutes old) immediately.
 *   2. Network fetch in the background; replace state on success.
 *   3. On error, return the supplied fallback.
 *
 * The fallback ensures the page renders even if a manifest is
 * missing, malformed, or returned a 404 — useful during early
 * deploys before all manifests have been authored.
 */
export function useContent<T>(path: string, fallback: T): T {
  const [value, setValue] = useState<T>(() => readCache<T>(path) ?? fallback);

  useEffect(() => {
    let cancelled = false;
    const url = `/content/${path}`;
    fetch(url, { cache: 'no-cache', credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Content fetch ${url} failed: ${res.status}`);
        const data = (await res.json()) as T;
        if (cancelled) return;
        writeCache(path, data);
        setValue(data);
      })
      .catch(() => {
        // Keep cached or fallback value in place. Logging suppressed
        // to avoid noise in the browser console for first-time loads.
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return value;
}

/**
 * Live API theme override (set via admin → /design-system "Adopt").
 * Returns nulls if the API hasn't been configured or the operator
 * has reset to theme.json. Cached for 60s in localStorage so first
 * paint isn't blocked on a network round-trip.
 */
interface ApiThemeResponse {
  light: ThemeContent['light'] | null;
  dark: ThemeContent['dark'] | null;
  pickedPalette?: string | null;
  pickedAt?: string | null;
}

function readApiThemeCache(): ApiThemeResponse | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(API_THEME_CACHE_KEY);
    if (!raw) return null;
    const env = JSON.parse(raw) as { expiresAt: number; value: ApiThemeResponse };
    if (!env || typeof env.expiresAt !== 'number') return null;
    if (Date.now() > env.expiresAt) return null;
    return env.value;
  } catch {
    return null;
  }
}

function writeApiThemeCache(value: ApiThemeResponse): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      API_THEME_CACHE_KEY,
      JSON.stringify({ expiresAt: Date.now() + API_THEME_TTL_MS, value }),
    );
  } catch {
    // Non-fatal — quota or private mode.
  }
}

function useApiThemeOverride(): ApiThemeResponse | null {
  const [value, setValue] = useState<ApiThemeResponse | null>(() => readApiThemeCache());

  useEffect(() => {
    let cancelled = false;
    const apiBase = getApiBaseUrl();
    fetch(`${apiBase}/api/theme`, { cache: 'no-cache', credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`api/theme ${res.status}`);
        const data = (await res.json()) as ApiThemeResponse;
        if (cancelled) return;
        writeApiThemeCache(data);
        setValue(data);
      })
      .catch(() => {
        // Live override is optional — falling back to /content/theme.json
        // is the entire point. Don't spam the console.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return value;
}

/**
 * Apply runtime theme overrides.
 *
 * Resolution order, highest priority first:
 *   1. Live API override (`/api/theme`) — set via /design-system "Adopt".
 *   2. Static manifest (`/content/theme.json`) — committed to the repo.
 *   3. Default tokens from `@rynora/theme` — already in the stylesheet.
 *
 * The merge is per-section (light, dark) so a partial API override still
 * lets theme.json supply whichever scheme it didn't override.
 */
export function useThemeOverrides(): void {
  const fileOverrides = useContent<ThemeContent>('theme.json', {});
  const apiOverride = useApiThemeOverride();

  const merged: ThemeContent = useMemo(() => {
    const light = (apiOverride && apiOverride.light) || fileOverrides.light;
    const dark = (apiOverride && apiOverride.dark) || fileOverrides.dark;
    return { light, dark };
  }, [fileOverrides, apiOverride]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const styleId = 'rynora-theme-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const lines: string[] = [];
    const writeBlock = (selector: string, vars: ThemeContent['light']) => {
      if (!vars) return;
      const decls: string[] = [];
      if (vars.accent) {
        decls.push(`--accent: ${vars.accent}`);
        decls.push(`--rynora-accent: ${vars.accent}`);
      }
      if (vars.accentStrong) {
        decls.push(`--accent-strong: ${vars.accentStrong}`);
        decls.push(`--rynora-accent-hover: ${vars.accentStrong}`);
        decls.push(`--accent-hover: ${vars.accentStrong}`);
      }
      if (vars.accentLight) decls.push(`--accent-light: ${vars.accentLight}`);
      if (vars.accentGlow) decls.push(`--accent-glow: ${vars.accentGlow}`);
      if (vars.gradientStart) decls.push(`--gradient-start: ${vars.gradientStart}`);
      if (vars.gradientEnd) decls.push(`--gradient-end: ${vars.gradientEnd}`);
      if (decls.length === 0) return;
      lines.push(`${selector} { ${decls.join('; ')}; }`);
    };

    writeBlock(':root', merged.light);
    writeBlock("[data-theme='dark']", merged.dark);

    if (lines.length === 0) return;
    const tag = document.createElement('style');
    tag.id = styleId;
    tag.textContent = lines.join('\n');
    root.appendChild(tag);

    return () => {
      const t = document.getElementById(styleId);
      if (t) t.remove();
    };
  }, [merged]);
}
